import { useCallback, useEffect, useRef, useState } from "react";
import { apiResourceUrl } from "../lib/api-fetch";

type DocumentSource = { inscriptionId: string; body?: never; mimeType?: never } | { body: Uint8Array; mimeType: string; inscriptionId?: never };

/** Same-origin permission is only for the fixed, external Ordinals origin. */
export function InscriptionDocumentFrame({ source, title, loading = "lazy" }: {
  source: DocumentSource;
  title: string;
  loading?: "lazy" | "eager";
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [previewState, setPreviewState] = useState("loading");
  const body = source.body;
  const mimeType = source.mimeType;
  const sendPreview = useCallback(() => {
    // The receiver has an opaque origin. Only file bytes and MIME are sent,
    // directly to this frame's Window; wallet/account state never enters it.
    if (body && mimeType) frame.current?.contentWindow?.postMessage({ type: "stc-inscription-preview", body, mimeType }, "*");
  }, [body, mimeType]);
  useEffect(sendPreview, [sendPreview]);
  useEffect(() => {
    if (!body) return;
    const ready = (event: MessageEvent<unknown>) => {
      if (event.source === frame.current?.contentWindow && event.data && typeof event.data === "object" && "type" in event.data && event.data.type === "stc-inscription-preview-ready") { setPreviewState("ready"); sendPreview(); }
    };
    window.addEventListener("message", ready);
    return () => window.removeEventListener("message", ready);
  }, [body, sendPreview]);
  return <iframe
    ref={frame}
    src={source.inscriptionId ? `https://ordinals.com/content/${encodeURIComponent(source.inscriptionId)}` : apiResourceUrl("/api/ordinals/preview")}
    title={title}
    loading={loading}
    data-preview-state={body ? previewState : undefined}
    onLoad={() => { setPreviewState(current => current === "ready" ? current : "loaded"); sendPreview(); }}
    sandbox={source.inscriptionId ? "allow-scripts allow-same-origin" : "allow-scripts"}
    referrerPolicy="no-referrer"
  />;
}

export function LocalInscriptionDocument({ body, mimeType, title, loading }: { body: Uint8Array; mimeType: string; title: string; loading?: "lazy" | "eager" }) {
  return <InscriptionDocumentFrame source={{ body, mimeType }} title={title} loading={loading} />;
}
